import {
  AuthError,
  ExpiredSessionError,
  LoginApiError5xx,
  TimeoutError,
} from '../client/error/errors';
import { Language } from '@yleisradio/areena-types';
import { logger } from '../common/logger';
import { fetchProfileCookie as fetchLoggedInUserCookie } from './user-profile-api';
import { Avatar, LoggedInUser, Mode, YleApiCredentials } from '../common/types';
import { isTestRuntime, withQuery, yleLoginCookieKey } from '../common/utils';
import { ABORT_SIGNAL_TIMEOUT } from '../server/constants';

const loginApiUrlTest = 'https://login.api-test.yle.fi/v1';
const loginApiUrlProduction = 'https://login.api.yle.fi/v1';

export const loginApiUrl = isTestRuntime()
  ? loginApiUrlTest
  : loginApiUrlProduction;

export interface DeviceTokenResponse {
  deviceToken: string;
  sessionId: string;
  qrCodeImage: string;
}

export type LoginFetch = (
  url: string,
  init?: RequestInit,
  forceRefresh?: boolean
) => Promise<Response>;

export const loginFetch = (
  mode: Mode,
  loginApiCredentials: YleApiCredentials
): LoginFetch => {
  return (url: string, requestInit?: RequestInit, forceRefresh?: boolean) => {
    if (loginApiCredentials.id && loginApiCredentials.key) {
      const queryParameters: { [key: string]: string } = {
        app_id: loginApiCredentials.id,
        app_key: loginApiCredentials.key,
      };

      if (forceRefresh) {
        queryParameters['refreshSession'] = 'force';
      }

      const init: RequestInit = {
        ...requestInit,
        mode: 'cors',
        credentials: 'include',
      };

      const timeoutPromise = new Promise<Response>((_, reject) => {
        setTimeout(() => {
          reject(
            new TimeoutError(
              `Request to ${url} timed out after ${ABORT_SIGNAL_TIMEOUT}ms`
            )
          );
        }, ABORT_SIGNAL_TIMEOUT);
      });

      return Promise.race([
        fetch(withQuery(url, queryParameters), init),
        timeoutPromise,
      ]).catch(err => {
        if (err instanceof TimeoutError) {
          logger.error(err);
          throw err;
        } else {
          logger.error({ err }, `LoginFetch failed ${url}`);
          throw new Error(`LoginFetch failed ${url} ${err}`);
        }
      });
    } else {
      throw new Error(`Can not create loginFetch. No credentials for ${mode}`);
    }
  };
};

export async function createDeviceToken(
  loginFetch: LoginFetch,
  deviceId: string,
  deviceName: string,
  isLuotunnusSubRoute: boolean,
  languageCode: Language
) {
  const url = `${loginApiUrl}/deviceToken?generateQR=true&language=${languageCode}&${isLuotunnusSubRoute ? '&register=true' : ''}`;

  const payload = {
    deviceId,
    deviceName,
  };

  const response = await loginFetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const baseErrorMessage =
    'Failed to initiate device pairing flow by creating deviceToken';

  // in theory this should never happen (and there is no way to handle this)
  if (response.status === 400) {
    logger.error(`${baseErrorMessage}. One or both required parameters were missing (deviceId, deviceName)
    Status: ${response.status}`);
  }

  if (response.status >= 500) {
    logger.error(`${baseErrorMessage}. Status: ${response.status}`);
    throw new LoginApiError5xx();
  }

  return (await response.json()) as DeviceTokenResponse;
}

export async function deviceTokenLogin(
  loginFetch: LoginFetch,
  sessionId: string
) {
  const url = `${loginApiUrl}/deviceToken/${sessionId}/login`;

  const response = await loginFetch(url, {
    method: 'POST',
  });

  return response;
}

export async function fetchUser(loginFetch: LoginFetch) {
  const url = `${loginApiUrl}/user/login`;

  const response = await loginFetch(url);

  if (response.status === 401) {
    throw new ExpiredSessionError();
  } else if (response.status !== 200) {
    throw new AuthError('Failed to request user info from login API');
  }

  const loggedInUser = (await response.json()) as LoggedInUser;

  return loggedInUser;
}

export const refreshSession = async (
  loginFetch: LoginFetch,
  loginCookie: string,
  refreshSession: boolean
) =>
  await loginFetch(
    `${loginApiUrl}/user/login`,
    {
      headers: { Cookie: loginCookie },
    },
    refreshSession
  );

export const logout = async (loginFetch: LoginFetch) =>
  await loginFetch(`${loginApiUrl}/user/login`, {
    method: 'DELETE',
  });

export async function emailLogin(
  loginFetch: LoginFetch,
  username: string,
  password: string,
  acceptedTosVersion?: string
): Promise<Response> {
  const url = `${loginApiUrl}/user/login`;

  const payload = [
    `username=${encodeURIComponent(username)}`,
    `password=${encodeURIComponent(password)}`,
  ];

  if (acceptedTosVersion) {
    payload.push(
      `accepted_tos_version=${encodeURIComponent(acceptedTosVersion)}`
    );
  }

  const body = payload.join('&');

  const response = await loginFetch(url, {
    method: 'POST',
    body,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
    },
  });

  return response;
}

export const acceptTermOfService = async (loginFetch: LoginFetch) => {
  const response = await loginFetch(`${loginApiUrl}/terms-of-service/accept`, {
    method: 'POST',
  });
  return response;
};

export async function fetchSubAccounts(
  loginFetch: LoginFetch,
  loginCookie: string
) {
  return await loginFetch(`${loginApiUrl}/sub-accounts`, {
    headers: { Cookie: loginCookie },
  });
}

export async function loginSubAccount(
  loginFetch: LoginFetch,
  loginCookie: string,
  userId: string
) {
  return await loginFetch(`${loginApiUrl}/sub-accounts/${userId}/login`, {
    method: 'POST',
    headers: { Cookie: loginCookie },
  });
}

/**
 * Intended to be called from client, so json-function is already called here.
 */
export async function getAvatars(loginFetch: LoginFetch): Promise<Avatar[]> {
  try {
    const response = await loginFetch(`${loginApiUrl}/avatars`);
    const avatars: unknown = await response.json();

    if (!Array.isArray(avatars)) {
      throw new Error('Invalid response');
    }

    // If avatars are in old format, convert them to new format
    if (typeof avatars[0] === 'string') {
      return avatars.map((id: string) => ({ id }));
    }

    return avatars as Avatar[];
  } catch (e) {
    logger.error(e);
    return [];
  }
}

export async function updateAccount(
  loginFetch: LoginFetch,
  loginCookie: string,
  updates: Pick<LoggedInUser, 'displayName' | 'profileColor'>
) {
  return await loginFetch(`${loginApiUrl}/account`, {
    method: 'PATCH',
    headers: {
      Cookie: `${yleLoginCookieKey}=${loginCookie};`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });
}

export async function updateSubAccount(
  loginFetch: LoginFetch,
  parentLoginCookie: string,
  subAccountId: string,
  updates: Pick<LoggedInUser, 'displayName'> & {
    avatar?: string | null;
    profileColor?: string | null;
  }
) {
  return await loginFetch(`${loginApiUrl}/sub-accounts/${subAccountId}`, {
    method: 'PATCH',
    headers: {
      Cookie: `${yleLoginCookieKey}=${parentLoginCookie};`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });
}

/**
 * Logs in and returns logged-in user defined by login cookie. Intended to be
 * used by the client.
 * @param loginFetch
 */
export async function fetchLoggedInUser(loginFetch: LoginFetch) {
  return fetchLoggedInUserCookie(loginFetch).then(async cookie => {
    if (!cookie) {
      throw new AuthError('No logged in user');
    }

    try {
      return await fetchUser(loginFetch);
    } catch (e) {
      // error thrown by fetchUserInfo does not contain session cookie
      if (e instanceof ExpiredSessionError) {
        throw new ExpiredSessionError(e.message, cookie[yleLoginCookieKey]);
      }

      throw e;
    }
  });
}

export async function fetchUserIdToken(loginFetch: LoginFetch) {
  return loginFetch(`${loginApiUrl}/user/id-token`);
}
